import { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, BoxProps, Button, FormLabel, InputBase, useTheme } from '@mui/material';
import { isNumber } from '@npi/buddy-utils';
import { useField } from 'formik';

export type OptionTypes = {
    value: string;
    label: string;
};

type PercentageCounterProps = {
    name: string;
    label?: string;
    boxStyling?: BoxProps;
    placeholder?: string;
    currency?: string;
    percentage?: boolean;
    disabled?: boolean;
};

export const PercentageCounter: React.FC<PercentageCounterProps> = ({
    label,
    boxStyling,
    placeholder,
    name,
    disabled,
}) => {
    const [{ value, onBlur }, { error, touched }, { setValue }] = useField(name);

    const [count, setCount] = useState(value);
    const { palette } = useTheme();

    useEffect(() => {
        if (!count) {
            return;
        }
        if (isNumber(Number(count))) {
            setValue(Number(count));
        } else {
            setValue(undefined);
            setCount(undefined);
        }
    }, [count, setValue]);

    // Increment the count value by +1
    const increment = () => {
        setCount((prevCount) => {
            return !Number.isNaN(prevCount) && (prevCount as unknown as string) !== ''
                ? Number.parseFloat(prevCount.toString()) + 1.0
                : 1.0;
        });
    };

    // Decrement the count value by -1
    const decrement = () => {
        if (count === 0) {
            return;
        }

        setCount((prevCount) => {
            return !Number.isNaN(prevCount) && (prevCount as unknown as string) !== ''
                ? Number.parseFloat(prevCount.toString()) - 1.0
                : 0.0;
        });
    };

    const hasError = error && touched;

    const borderColor = hasError ? 'red' : palette.primary.main;
    return (
        <Box display="flex" flexDirection="column" {...boxStyling}>
            {label && (
                <FormLabel sx={{ marginBottom: '8px', color: '#000', fontWeight: 600 }}>
                    {label}
                </FormLabel>
            )}

            <Box
                display="flex"
                flexDirection="row"
                border={`0px solid ${borderColor}`}
                overflow="hidden"
                gap={2}
                alignItems="center"
            >
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    bgcolor="#F3A256"
                    height="32px"
                    width="32px"
                    borderRadius="8px"
                    color="white"
                    fontSize="32px"
                >
                    <Button
                        sx={{
                            width: '32px',
                            height: '100%',
                            padding: 0,
                            color: 'white',
                            fontSize: '32px',
                        }}
                        onClick={decrement}
                        disabled={disabled}
                    >
                        <RemoveIcon />
                    </Button>
                </Box>
                <InputBase
                    name={name}
                    value={count}
                    onBlur={onBlur}
                    onChange={(e) => {
                        setCount(e.target.value);
                    }}
                    placeholder={placeholder || label}
                    inputMode="decimal"
                    style={{
                        paddingLeft: '0px',
                        paddingRight: '0px',
                    }}
                    inputProps={{
                        className: 'MuiInputBase-input',
                        style: {
                            textAlign: 'center',
                            borderWidth: 0,
                            padding: '16px 14px',
                            width: '100%',
                            borderBottom: `1px solid ${palette.primary.main}`,
                            position: 'relative',
                        },
                        onFocus: (e) => {
                            e.target.style.borderBottom = `2px solid ${palette.primary.main}`;
                        },
                        onBlur: (e) => {
                            e.target.style.borderBottom = `1px solid ${palette.primary.main}`;
                        },
                    }}
                    sx={{
                        borderBottomColor: palette.primary.main,
                        '&.MuiInputBase-root': {
                            border: 'none !important',
                            '&:hover': {
                                border: 'none !important',
                            },
                            '&.Mui-focused': {
                                border: 'none !important',
                            },
                        },
                        '& input[type=number]': {
                            '-moz-appearance': 'textfield',
                        },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: (props) => {
                                    return props.palette.primary.main;
                                },
                            },
                            '&:hover fieldset': {
                                borderColor: (props) => {
                                    return props.palette.primary.main;
                                },
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: (props) => {
                                    return props.palette.primary.main;
                                },
                            },
                        },
                    }}
                    autoComplete="off"
                    className={`form-input ${touched && error ? 'input-error' : ''}`}
                    disabled={disabled}
                />

                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    bgcolor={palette.primary.main}
                    height="32px"
                    width="32px"
                    borderRadius="8px"
                    color="white"
                    fontSize="32px"
                >
                    <Button
                        sx={{
                            width: '32px',
                            height: '100%',
                            padding: 0,
                            color: 'white',
                            fontSize: '32px',
                        }}
                        onClick={increment}
                        disabled={disabled}
                    >
                        <AddIcon />
                    </Button>
                </Box>
            </Box>
            {hasError && <label className="error">{error}</label>}
        </Box>
    );
};
